/**
 * Formats a number with comma separators for thousands
 * @param value - The value to format (can be number, string, null, undefined)
 * @param maxDecimals - Maximum number of decimal places (default: 4)
 * @param preserveTrailingZeros - Whether to preserve trailing zeros (default: false)
 * @returns Formatted string with comma separators
 */
export const formatNumberWithCommas = (
  value: number | string | null | undefined,
  maxDecimals: number = 4,
  preserveTrailingZeros: boolean = false
): string => {
  // Handle null, undefined, or empty string
  if (value === null || value === undefined || value === '') {
    return '';
  }

  // Convert to string and handle directly
  const stringValue = String(value);

  // Check if it's an integer (no decimal point)
  if (!stringValue.includes('.')) {
    // Format integer with commas
    const num = parseInt(stringValue, 10);
    if (isNaN(num)) return stringValue;
    return num.toLocaleString();
  }

  // For decimal values, split and limit decimal places
  const [whole, decimal] = stringValue.split('.');

  // Parse the whole number part and format with commas
  const wholeNum = parseInt(whole, 10);
  if (isNaN(wholeNum)) return stringValue;
  const formattedWhole = wholeNum.toLocaleString();

  // Handle decimal part
  let limitedDecimal = decimal.slice(0, maxDecimals);

  // If preserveTrailingZeros is false, trim trailing zeros
  if (!preserveTrailingZeros) {
    while (limitedDecimal.endsWith('0')) {
      limitedDecimal = limitedDecimal.slice(0, -1);
    }
  }

  // If decimal part is empty after trimming, return just the whole number
  if (limitedDecimal === '') {
    return formattedWhole;
  }

  // Return the formatted whole number with the decimal part
  return `${formattedWhole}.${limitedDecimal}`;
};

/**
 * Parses a number string that may contain comma separators, whitespace, or hidden characters
 * @param value - The string value to parse (e.g., "123,456.78", "4.5 ", " 1,234 ")
 * @param preserveTrailingZeros - Whether to preserve trailing zeros in decimal part
 * @returns Parsed number or string (if preserving trailing zeros) or NaN if invalid
 */
export const parseNumberWithCommas = (
  value: string,
  preserveTrailingZeros: boolean = false
): number | string => {
  if (!value || typeof value !== 'string') {
    return NaN;
  }

  // Clean the value: trim whitespace, remove commas, and clean hidden/weird characters
  const cleanValue = value
    .trim() // Remove leading/trailing whitespace
    .replace(/,/g, '') // Remove commas
    .replace(/[\u00A0\u2000-\u200B\u2028\u2029\uFEFF]/g, '') // Remove non-breaking spaces and other hidden chars
    .replace(/[^\d.-]/g, ''); // Keep only digits, dots, and minus signs

  // If preserving trailing zeros, return the clean string if it's a valid number
  if (preserveTrailingZeros) {
    const numValue = parseFloat(cleanValue);
    if (isNaN(numValue)) {
      return NaN;
    }
    return cleanValue;
  }

  return parseFloat(cleanValue);
};

/**
 * Sanitizes a string value by removing whitespace and hidden characters commonly found in copy/paste operations
 * @param value - The string value to sanitize
 * @returns Cleaned string value or empty string if null/undefined
 */
export const sanitizeStringValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }

  return String(value)
    .trim() // Remove leading/trailing whitespace
    .replace(/[\u00A0\u2000-\u200B\u2028\u2029\uFEFF]/g, ' ') // Replace non-breaking spaces with regular spaces
    .replace(/\s+/g, ' ') // Collapse multiple spaces into single space
    .trim(); // Trim again after space normalization
};

/**
 * Formats a percentage value with comma separators
 * @param value - The percentage value to format
 * @param decimals - Number of decimal places for percentage (default: 2)
 * @returns Formatted percentage string with comma separators
 */
export const formatPercentageWithCommas = (
  value: number | null | undefined,
  decimals: number = 2
): string => {
  if (value === null || value === undefined || isNaN(Number(value))) {
    return '';
  }

  const numValue = Number(value);
  const formattedValue = Math.abs(numValue).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  const sign = numValue > 0 ? '+' : numValue < 0 ? '-' : '';
  return `${sign}${formattedValue}%`;
};
